import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CableRoundedIcon from "@mui/icons-material/CableRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ComputerRoundedIcon from "@mui/icons-material/ComputerRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getApiError } from "../api/errors";
import { lookupNativePosBarcode } from "../api/nativePos";
import {
  createStoreBridgePairingCode,
  getStoreBridgeDevices,
  getStoreBridgeJobStatus,
  queueStoreBridgeTestDrawer,
  queueStoreBridgeTestReceipt,
  revokeStoreBridgeDevice,
  updateStoreBridgeSettings,
} from "../api/storeBridge";
import PageHeader from "../components/PageHeader";
import OfflinePosDeviceCard from "../components/OfflinePosDeviceCard";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { useAIPageContext } from "../context/AIAssistantContext";
import {
  clearPrinterReadiness,
  clearScannerReadiness,
  getPrinterReadiness,
  getPrinterSettings,
  getScannerReadiness,
  setPrinterPaperWidth,
  setPrinterReadiness,
  setScannerReadiness,
} from "../utils/hardwareReadiness";
import { printTestReceipt } from "../utils/receiptPrinter";
import {
  getSelectedStoreBridgeDeviceId,
  setSelectedStoreBridgeDeviceId,
} from "../utils/storeBridge";

function when(value, t) {
  if (!value) return t("hardware.never");
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? t("hardware.previously") : parsed.toLocaleString();
}

function bridgeSettings(device) {
  return {
    printer_name: device?.settings?.printer_name || "",
    print_mode: device?.settings?.print_mode || "windows_driver",
    paper_width_mm: Number(device?.settings?.paper_width_mm) === 58 ? 58 : 80,
    auto_print: Boolean(device?.settings?.auto_print),
    drawer_enabled: Boolean(device?.settings?.drawer_enabled),
    drawer_pin: Number(device?.settings?.drawer_pin) === 1 ? 1 : 0,
    drawer_pulse_on_ms: Number(device?.settings?.drawer_pulse_on_ms || 100),
    drawer_pulse_off_ms: Number(device?.settings?.drawer_pulse_off_ms || 250),
  };
}

export default function HardwareSetup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedWorkspace, selectedStore, canManage } = useAuth();
  const scannerRef = useRef(null);

  const workspaceId = selectedWorkspace?.public_id || "";
  const storeId = selectedStore?.public_id || "";

  const [scannerCode, setScannerCode] = useState("");
  const [scannerBusy, setScannerBusy] = useState(false);
  const [scannerResult, setScannerResult] = useState(null);
  const [scannerReady, setScannerReady] = useState(null);

  const [browserPrinterReady, setBrowserPrinterReady] = useState(null);
  const [browserPaperWidth, setBrowserPaperWidth] = useState(80);
  const [browserPrinterMessage, setBrowserPrinterMessage] = useState(null);

  const [devices, setDevices] = useState([]);
  const [devicesBusy, setDevicesBusy] = useState(false);
  const [bridgeError, setBridgeError] = useState("");
  const [pairing, setPairing] = useState(null);
  const [pairingName, setPairingName] = useState("Front cashier");
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [browserBridgeDeviceId, setBrowserBridgeDeviceId] = useState(null);
  const [settings, setSettings] = useState(bridgeSettings(null));
  const [savingSettings, setSavingSettings] = useState(false);
  const [jobMessage, setJobMessage] = useState(null);
  useAIPageContext({ version: 1, page_id: "hardware_setup", section: selectedDeviceId ? "printer" : "overview" });

  const selectedDevice = useMemo(
    () => devices.find((device) => device.device_id === selectedDeviceId) || null,
    [devices, selectedDeviceId],
  );

  const browserBridgeDevice = useMemo(
    () => devices.find((device) => device.device_id === browserBridgeDeviceId) || null,
    [browserBridgeDeviceId, devices],
  );

  const selectedDeviceIsBrowserDevice = Boolean(
    selectedDevice && selectedDevice.device_id === browserBridgeDeviceId,
  );

  const directReady = Boolean(
    browserBridgeDevice
      && browserBridgeDevice.status === "active"
      && browserBridgeDevice.online
      && browserBridgeDevice.settings?.printer_name,
  );
  const printerReady = directReady || Boolean(browserPrinterReady);
  const workstationReady = Boolean(scannerReady && printerReady);

  const loadDevices = useCallback(async () => {
    if (!storeId) return;
    setDevicesBusy(true);
    setBridgeError("");
    try {
      const rows = await getStoreBridgeDevices();
      setDevices(rows);
      const localSelected = getSelectedStoreBridgeDeviceId(workspaceId, storeId);
      setBrowserBridgeDeviceId(localSelected || null);
      const next = rows.find((row) => row.device_id === localSelected)
        || rows.find((row) => row.status === "active")
        || rows[0]
        || null;
      setSelectedDeviceId(next?.device_id || null);
      if (next) setSettings(bridgeSettings(next));
    } catch (error) {
      setBridgeError(getApiError(error, t("hardware.deviceLoadError")));
    } finally {
      setDevicesBusy(false);
    }
  }, [storeId, workspaceId, t]);

  useEffect(() => {
    setScannerReady(getScannerReadiness(workspaceId, storeId));
    setBrowserBridgeDeviceId(getSelectedStoreBridgeDeviceId(workspaceId, storeId));
    const printer = getPrinterSettings(workspaceId, storeId);
    setBrowserPrinterReady(getPrinterReadiness(workspaceId, storeId));
    setBrowserPaperWidth(printer.paper_width_mm);
    setScannerCode("");
    setScannerResult(null);
    setBrowserPrinterMessage(null);
    setPairing(null);
    setJobMessage(null);
    loadDevices();
    const timer = window.setTimeout(() => scannerRef.current?.focus(), 120);
    return () => window.clearTimeout(timer);
  }, [loadDevices, storeId, workspaceId]);

  useEffect(() => {
    if (selectedDevice) setSettings(bridgeSettings(selectedDevice));
  }, [selectedDevice]);

  const testScanner = async (event) => {
    event.preventDefault();
    const code = scannerCode.trim();
    if (!code || scannerBusy) return;
    setScannerBusy(true);
    setScannerResult(null);
    try {
      const response = await lookupNativePosBarcode(code);
      const item = response.data || {};
      const record = setScannerReadiness(workspaceId, storeId, {
        matched_product: true,
        barcode: code,
        product_public_id: item.product_public_id,
        product_name: item.product_name,
        sku: item.sku,
      });
      setScannerReady(record);
      setScannerResult({ severity: "success", text: t("hardware.matched", { name: `${item.product_name}${item.sku ? ` • ${item.sku}` : ""}` }) });
      setScannerCode("");
    } catch (error) {
      if (error?.response?.status === 404) {
        const record = setScannerReadiness(workspaceId, storeId, { matched_product: false, barcode: code });
        setScannerReady(record);
        setScannerResult({ severity: "warning", text: t("hardware.scannerUnassigned") });
        setScannerCode("");
      } else {
        setScannerResult({ severity: "error", text: getApiError(error, t("hardware.scannerError")) });
      }
    } finally {
      setScannerBusy(false);
      window.setTimeout(() => scannerRef.current?.focus(), 0);
    }
  };

  const generatePairing = async () => {
    setBridgeError("");
    setPairing(null);
    try {
      setPairing(await createStoreBridgePairingCode(pairingName.trim() || t("hardware.cashierWorkstation")));
      await loadDevices();
    } catch (error) {
      setBridgeError(getApiError(error, t("hardware.pairError")));
    }
  };

  const chooseDevice = (deviceId) => {
    setSelectedDeviceId(deviceId || null);
    const device = devices.find((row) => row.device_id === deviceId) || null;
    setSettings(bridgeSettings(device));
    setJobMessage(null);
  };

  const useOnThisWorkstation = () => {
    if (!selectedDeviceId) return;
    setSelectedStoreBridgeDeviceId(workspaceId, storeId, selectedDeviceId);
    setBrowserBridgeDeviceId(selectedDeviceId);
    setJobMessage({ severity: "success", text: t("hardware.usingNotice") });
  };

  const saveSettings = async () => {
    if (!selectedDeviceId || !canManage) return;
    setSavingSettings(true);
    setBridgeError("");
    try {
      const updated = await updateStoreBridgeSettings(selectedDeviceId, {
        ...settings,
        printer_name: settings.printer_name || null,
      });
      setDevices((current) => current.map((row) => row.device_id === updated.device_id ? updated : row));
      setSettings(bridgeSettings(updated));
      setJobMessage({ severity: "success", text: t("hardware.saved") });
    } catch (error) {
      setBridgeError(getApiError(error, t("hardware.settingsError")));
    } finally {
      setSavingSettings(false);
    }
  };

  const waitForBridgeJob = async (deviceId, jobId, successText) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < 30000) {
      const current = await getStoreBridgeJobStatus(deviceId, jobId);
      if (current.status === "completed") {
        setJobMessage({ severity: "success", text: successText });
        return current;
      }
      if (current.status === "failed" || current.status === "cancelled") {
        setJobMessage({
          severity: "error",
          text: current.last_error || t("hardware.jobStatus", { status: current.status }),
        });
        return current;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 850));
    }
    setJobMessage({
      severity: "warning",
      text: t("hardware.jobQueued"),
    });
    return null;
  };

  const testDirectReceipt = async () => {
    if (!selectedDeviceId) return;
    setJobMessage(null);
    try {
      const result = await queueStoreBridgeTestReceipt(selectedDeviceId);
      setJobMessage({ severity: "info", text: t("hardware.receiptQueued", { id: result.job_id }) });
      await waitForBridgeJob(
        selectedDeviceId,
        result.job_id,
        t("hardware.receiptConfirmed"),
      );
    } catch (error) {
      setJobMessage({ severity: "error", text: getApiError(error, t("hardware.receiptError")) });
    }
  };

  const testDrawer = async () => {
    if (!selectedDeviceId) return;
    setJobMessage(null);
    try {
      const result = await queueStoreBridgeTestDrawer(selectedDeviceId);
      setJobMessage({ severity: "info", text: t("hardware.drawerQueued", { id: result.job_id }) });
      await waitForBridgeJob(
        selectedDeviceId,
        result.job_id,
        t("hardware.drawerConfirmed"),
      );
    } catch (error) {
      setJobMessage({ severity: "error", text: getApiError(error, t("hardware.drawerError")) });
    }
  };

  const revokeDevice = async () => {
    if (!selectedDeviceId || !canManage) return;
    if (!window.confirm(t("hardware.revokeConfirm"))) return;
    try {
      await revokeStoreBridgeDevice(selectedDeviceId);
      if (getSelectedStoreBridgeDeviceId(workspaceId, storeId) === selectedDeviceId) {
        setSelectedStoreBridgeDeviceId(workspaceId, storeId, null);
      }
      setSelectedDeviceId(null);
      await loadDevices();
    } catch (error) {
      setBridgeError(getApiError(error, t("hardware.revokeError")));
    }
  };

  const runBrowserPrinterTest = () => {
    setBrowserPrinterMessage(null);
    const opened = printTestReceipt({
      storeName: selectedStore?.name || "ABOS Store",
      paperWidthMm: browserPaperWidth,
    });
    if (!opened) {
      setBrowserPrinterMessage({ severity: "error", text: t("hardware.printBlocked") });
      return;
    }
    if (window.confirm(t("hardware.physicalConfirm"))) {
      const record = setPrinterReadiness(workspaceId, storeId, { paper_width_mm: browserPaperWidth });
      setBrowserPrinterReady(record);
      setBrowserPrinterMessage({ severity: "success", text: t("hardware.fallbackVerified") });
    }
  };

  return (
    <MainLayout>
      <PageHeader
        eyebrow={t("hardware.eyebrow")}
        title={t("nav.hardware")}
        description={t("hardware.subtitle")}
        status={{ label: t(workstationReady ? "hardware.ready" : "hardware.incomplete"), color: workstationReady ? "success" : "warning", variant: "outlined" }}
        actions={<Button variant="contained" startIcon={<PointOfSaleRoundedIcon />} onClick={() => navigate("/pos")}>{t("hardware.openPos")}</Button>}
      />

      <Alert severity="info" sx={{ mb: 2.5, borderRadius: 3 }}>
        {t("hardware.agentNotice")}
      </Alert>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "minmax(340px,.72fr) minmax(0,1.28fr)" }, gap: 2.5, alignItems: "start" }}>
        <Stack spacing={2.25}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 2.6 }}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <ComputerRoundedIcon color="primary" />
                <Typography variant="h6">{t("hardware.workstation")}</Typography>
              </Stack>
              <Stack spacing={1.1} sx={{ mt: 1.8 }}>
                <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 3 }}>
                  <Stack direction="row" justifyContent="space-between"><Typography fontWeight={900}>{t("hardware.scanner")}</Typography><Chip size="small" color={scannerReady ? "success" : "default"} label={t(scannerReady ? "hardware.verified" : "hardware.notVerified")} /></Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{scannerReady?.product_name ? t("hardware.lastMatch", { name: scannerReady.product_name }) : t("hardware.scannerType")}</Typography>
                </Box>
                <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 3 }}>
                  <Stack direction="row" justifyContent="space-between"><Typography fontWeight={900}>{t("hardware.receiptPrinting")}</Typography><Chip size="small" color={printerReady ? "success" : "default"} label={t(directReady ? "hardware.directBridge" : browserPrinterReady ? "hardware.browserVerified" : "hardware.notReady")} /></Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{directReady ? `${selectedDevice?.settings?.printer_name} • ${selectedDevice?.settings?.paper_width_mm} mm` : t("hardware.browserFallbackWidth", { width: browserPaperWidth })}</Typography>
                </Box>
                <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 3 }}>
                  <Stack direction="row" justifyContent="space-between"><Typography fontWeight={900}>{t("hardware.cashDrawer")}</Typography><Chip size="small" color={directReady && selectedDevice?.settings?.drawer_enabled ? "success" : "default"} label={t(directReady && selectedDevice?.settings?.drawer_enabled ? "hardware.enabled" : "hardware.optional")} /></Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{t("hardware.drawerHelp")}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 2.6 }}>
              <Stack direction="row" spacing={1.1} alignItems="center"><QrCodeScannerRoundedIcon color="primary" /><Typography variant="h6">{t("hardware.scannerTest")}</Typography></Stack>
              {scannerResult && <Alert severity={scannerResult.severity} sx={{ mt: 1.5 }}>{scannerResult.text}</Alert>}
              <Box component="form" onSubmit={testScanner} sx={{ mt: 1.5 }}>
                <TextField
                  fullWidth
                  inputRef={scannerRef}
                  value={scannerCode}
                  disabled={scannerBusy}
                  onChange={(event) => setScannerCode(event.target.value)}
                  placeholder={t("hardware.scannerPlaceholder")}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><KeyboardRoundedIcon /></InputAdornment>,
                    endAdornment: scannerBusy ? <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment> : null,
                  }}
                />
              </Box>
              <Stack direction="row" spacing={1} sx={{ mt: 1.3 }}>
                <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={() => { clearScannerReadiness(workspaceId, storeId); setScannerReady(null); }}>{t("hardware.reset")}</Button>
                <Button size="small" startIcon={<Inventory2RoundedIcon />} onClick={() => navigate("/products")}>{t("hardware.manageBarcodes")}</Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Stack spacing={2.5}>
          <Card sx={{ borderRadius: 4, overflow: "hidden" }}>
            <Box sx={{ px: 3, py: 2.4, bgcolor: "#0f172a", color: "white" }}>
              <Stack direction="row" spacing={1.4} alignItems="center">
                <Box sx={{ width: 48, height: 48, borderRadius: 2.6, display: "grid", placeItems: "center", bgcolor: "rgba(37,99,235,.22)", color: "#93c5fd" }}><CableRoundedIcon /></Box>
                <Box><Typography variant="overline" sx={{ color: "#93c5fd", fontWeight: 900 }}>{t("hardware.directControl")}</Typography><Typography variant="h5" color="white">ABOS Store Bridge</Typography></Box>
              </Stack>
            </Box>
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t("hardware.bridgeRecommendation")}
              </Alert>
              {bridgeError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBridgeError("")}>{bridgeError}</Alert>}
              {jobMessage && <Alert severity={jobMessage.severity} sx={{ mb: 2 }}>{jobMessage.text}</Alert>}

              {canManage && (
                <Box sx={{ p: 2, border: "1px solid #dbeafe", bgcolor: "#f8fbff", borderRadius: 3, mb: 2 }}>
                  <Typography fontWeight={900}>{t("hardware.pair")}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: .4 }}>{t("hardware.pairHelp")}</Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.3 }}>
                    <TextField size="small" label={t("hardware.workstationName")} value={pairingName} onChange={(event) => setPairingName(event.target.value)} sx={{ minWidth: 220 }} />
                    <Button variant="contained" startIcon={<CableRoundedIcon />} onClick={generatePairing}>{t("hardware.generateCode")}</Button>
                  </Stack>
                  {pairing && (
                    <Alert severity="info" sx={{ mt: 1.3 }}>
                      <Typography variant="caption" color="text.secondary">{t("hardware.pairingCode")}</Typography>
                      <Typography variant="h5" sx={{ letterSpacing: 2, fontFamily: "monospace", mt: .3 }}>{pairing.pairing_code}</Typography>
                      <Typography variant="caption">{t("hardware.expires", { date: when(pairing.pairing_expires_at, t) })}</Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {browserBridgeDevice ? (
                <Alert
                  severity={directReady ? "success" : "warning"}
                  icon={directReady ? <CheckCircleRoundedIcon /> : undefined}
                  sx={{ mb: 2 }}
                >
                  <Typography fontWeight={900}>
                    {t("hardware.browserUsing", { name: browserBridgeDevice.name })}
                  </Typography>
                  <Typography variant="body2">
                    {browserBridgeDevice.hostname || t("hardware.unknownPc")}
                    {browserBridgeDevice.settings?.printer_name
                      ? ` • ${t("hardware.printer", { name: browserBridgeDevice.settings.printer_name })}`
                      : ` • ${t("hardware.noPrinter")}`}
                    {` • ${t(browserBridgeDevice.online ? "hardware.online" : "hardware.offline")}`}
                  </Typography>
                  {!directReady && (
                    <Typography variant="body2" sx={{ mt: .4 }}>
                      {t("hardware.bridgeNotReady")}
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography fontWeight={900}>{t("hardware.noBridge")}</Typography>
                  <Typography variant="body2">
                    {t("hardware.noBridgeHelp")}
                  </Typography>
                </Alert>
              )}

              <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} alignItems={{ xs: "stretch", md: "center" }}>
                <FormControl size="small" sx={{ minWidth: 270, flex: 1 }}>
                  <InputLabel>{t("hardware.bridgeDevice")}</InputLabel>
                  <Select label={t("hardware.bridgeDevice")} value={selectedDeviceId || ""} onChange={(event) => chooseDevice(event.target.value)} disabled={devicesBusy}>
                    {devices.map((device) => (
                      <MenuItem value={device.device_id} key={device.device_id}>
                        {device.name} • {device.online ? t("hardware.online") : device.status}
                        {device.device_id === browserBridgeDeviceId ? ` • ${t("hardware.thisBrowser")}` : ""}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" startIcon={devicesBusy ? <CircularProgress size={16} /> : <RefreshRoundedIcon />} onClick={loadDevices}>{t("catalog.refresh")}</Button>
                {selectedDevice && <Chip color={selectedDevice.online ? "success" : "warning"} label={t(selectedDevice.online ? "hardware.online" : "hardware.offline")} />}
              </Stack>

              {selectedDevice && (
                <Box sx={{ mt: 2, p: 2, border: "1px solid #e2e8f0", borderRadius: 3 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                    <Box>
                      <Typography fontWeight={900}>{selectedDevice.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{t("hardware.deviceMeta", { host: selectedDevice.hostname || t("hardware.unknownPc"), version: selectedDevice.version || "-", date: when(selectedDevice.last_seen_at, t) })}</Typography>
                    </Box>
                    {selectedDeviceIsBrowserDevice ? (
                      <Chip
                        color="success"
                        icon={<CheckCircleRoundedIcon />}
                        label={t("hardware.usingBrowser")}
                        sx={{ fontWeight: 850, alignSelf: { xs: "flex-start", sm: "center" } }}
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ComputerRoundedIcon />}
                        onClick={useOnThisWorkstation}
                      >
                        {t("hardware.useBrowser")}
                      </Button>
                    )}
                  </Stack>

                  <Divider sx={{ my: 2 }} />
                  <Typography fontWeight={900}>{t("hardware.configurePrinter")}</Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.4fr 1fr" }, gap: 1.2, mt: 1.2 }}>
                    <FormControl size="small">
                      <InputLabel>{t("hardware.installedPrinter")}</InputLabel>
                      <Select label={t("hardware.installedPrinter")} value={settings.printer_name} onChange={(event) => setSettings((v) => ({ ...v, printer_name: event.target.value }))} disabled={!canManage}>
                        <MenuItem value=""><em>{t("hardware.selectPrinter")}</em></MenuItem>
                        {(selectedDevice.printers || []).map((printer) => <MenuItem key={printer} value={printer}>{printer}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <FormControl size="small">
                      <InputLabel>{t("hardware.printMode")}</InputLabel>
                      <Select label={t("hardware.printMode")} value={settings.print_mode} onChange={(event) => setSettings((v) => ({ ...v, print_mode: event.target.value }))} disabled={!canManage}>
                        <MenuItem value="windows_driver">{t("hardware.windowsDriver")}</MenuItem>
                        <MenuItem value="escpos_raw">{t("hardware.escPos")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }} sx={{ mt: 1.6 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={800}>{t("hardware.paperWidth")}</Typography>
                      <ToggleButtonGroup exclusive size="small" value={settings.paper_width_mm} onChange={(_, value) => value && setSettings((v) => ({ ...v, paper_width_mm: value }))} disabled={!canManage} sx={{ mt: .5 }}>
                        <ToggleButton value={58}>58 mm</ToggleButton><ToggleButton value={80}>80 mm</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <FormControlLabel control={<Switch checked={settings.auto_print} onChange={(event) => setSettings((v) => ({ ...v, auto_print: event.target.checked }))} disabled={!canManage} />} label={t("hardware.autoPrint")} />
                    <FormControlLabel control={<Switch checked={settings.drawer_enabled} onChange={(event) => setSettings((v) => ({ ...v, drawer_enabled: event.target.checked }))} disabled={!canManage} />} label={t("hardware.openDrawer")} />
                  </Stack>

                  {settings.drawer_enabled && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      {t("hardware.drawerWarning")}
                    </Alert>
                  )}

                  {settings.print_mode === "escpos_raw" && (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      {t("hardware.rawWarning")}
                    </Alert>
                  )}

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1.7 }}>
                    {canManage && <Button variant="contained" startIcon={<SettingsRoundedIcon />} disabled={savingSettings} onClick={saveSettings}>{t(savingSettings ? "hardware.saving" : "hardware.saveSettings")}</Button>}
                    <Button variant="outlined" startIcon={<PrintRoundedIcon />} disabled={!selectedDevice.online || !settings.printer_name} onClick={testDirectReceipt}>{t("hardware.testSilent")}</Button>
                    <Button variant="outlined" color="warning" disabled={!selectedDevice.online || !settings.printer_name || !settings.drawer_enabled} onClick={testDrawer}>{t("hardware.testDrawer")}</Button>
                    {canManage && <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={revokeDevice}>{t("hardware.revoke")}</Button>}
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          <OfflinePosDeviceCard />

          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 2.6 }}>
              <Stack direction="row" spacing={1.1} alignItems="center"><ReceiptLongRoundedIcon color="success" /><Typography variant="h6">{t("hardware.browserFallback")}</Typography></Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>{t("hardware.fallbackHelp")}</Typography>
              {browserPrinterMessage && <Alert severity={browserPrinterMessage.severity} sx={{ mt: 1.4 }}>{browserPrinterMessage.text}</Alert>}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ xs: "stretch", sm: "center" }} sx={{ mt: 1.5 }}>
                <ToggleButtonGroup exclusive size="small" value={browserPaperWidth} onChange={(_, value) => { if (!value) return; const record = setPrinterPaperWidth(workspaceId, storeId, value); setBrowserPaperWidth(record?.paper_width_mm || value); setBrowserPrinterReady(record?.verified_at ? record : null); }}>
                  <ToggleButton value={58}>58 mm</ToggleButton><ToggleButton value={80}>80 mm</ToggleButton>
                </ToggleButtonGroup>
                <Button variant="outlined" color="success" startIcon={<PrintRoundedIcon />} onClick={runBrowserPrinterTest}>{t("hardware.printBrowserTest")}</Button>
                {browserPrinterReady && <Button size="small" onClick={() => { clearPrinterReadiness(workspaceId, storeId); setBrowserPrinterReady(null); }}>{t("hardware.resetFallback")}</Button>}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </MainLayout>
  );
}
